import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, API } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  Loader2, ChevronRight, MapPin, Clock, Wallet, CalendarDays, Plane,
  Languages, Coins, Check, ArrowRight, List, Sparkles,
} from "lucide-react";

const FIELD_LABELS = {
  pays: { label: "Pays", icon: MapPin },
  region: { label: "Région", icon: MapPin },
  continent: { label: "Continent", icon: MapPin },
  meilleure_periode: { label: "Meilleure période", icon: CalendarDays },
  budget_moyen: { label: "Budget moyen", icon: Wallet },
  duree_vol: { label: "Durée de vol", icon: Plane },
  duree_conseillee: { label: "Durée conseillée", icon: Clock },
  langue: { label: "Langue", icon: Languages },
  monnaie: { label: "Monnaie", icon: Coins },
  decalage_horaire: { label: "Décalage horaire", icon: Clock },
};

const RELATED_TITLES = {
  destination: "Destinations similaires",
  guide: "Guides liés",
  honeymoon: "Voyages de noces",
  sur_mesure: "Voyages sur mesure",
  circuit: "Circuits",
  hotel: "Hôtels d'exception",
  inspiration: "Inspirations",
  ville: "Villes à découvrir",
  pays: "Pays",
  region: "Régions",
  conseils: "Conseils pratiques",
  faq: "Questions fréquentes",
};

const absUrl = (u) => {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  return `${window.location.origin}${u.startsWith("/") ? "" : "/"}${u}`;
};

export default function PublicContent({ contentType }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let active = true;
    setState("loading");
    (async () => {
      try {
        const { data: d } = await api.get(`/public/content/${contentType}/${slug}`);
        if (active) { setData(d); setState("ok"); }
      } catch {
        // maybe the slug changed → follow a 301 redirect if one exists
        try {
          const { data: r } = await api.get("/public/redirect", { params: { path: `/${contentTypePrefix(contentType)}/${slug}` } });
          if (active && r?.to_path) { navigate(r.to_path, { replace: true }); return; }
        } catch { /* ignore */ }
        if (active) setState("notfound");
      }
    })();
    return () => { active = false; };
  }, [contentType, slug, navigate]);

  if (state === "loading") {
    return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#96836B]" /></div>;
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <Nav />
        <div className="pt-40 pb-32 text-center px-6">
          <p className="label-eyebrow">Erreur 404</p>
          <h1 className="font-serif-display text-4xl mt-4">Cette page n'existe pas</h1>
          <p className="text-[#737373] mt-3">Le contenu recherché est introuvable ou a été déplacé.</p>
          <Link to="/" className="inline-block mt-8 border border-[#1A1B18] px-6 py-3 text-[11px] uppercase tracking-[0.24em] hover:bg-[#1A1B18] hover:text-white transition-colors">Retour à l'accueil</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return <ContentPage data={data} />;
}

function contentTypePrefix(type) {
  const map = {
    destination: "destinations", guide: "guides", faq: "faq",
    honeymoon: "voyages-de-noces", sur_mesure: "voyages-sur-mesure",
    circuit: "circuits", hotel: "hotels", inspiration: "inspirations",
    pays: "pays", region: "regions", ville: "villes", conseils: "conseils", blog: "blog",
  };
  return map[type] || "content";
}

const ContentPage = ({ data }) => {
  const seo = data.seo || {};
  const metaTitle = seo.meta_title || data.title;
  const metaDesc = seo.meta_description || data.excerpt || "";
  const canonical = seo.canonical_url || absUrl(data.url);
  const ogImage = data.cover_image ? absUrl(data.cover_image) : null;

  const sections = (data.sections || []).filter((s) => s.visible !== false);
  const facts = useMemo(() => Object.entries(FIELD_LABELS)
    .filter(([k]) => data.fields?.[k])
    .map(([k, meta]) => ({ key: k, ...meta, value: data.fields[k] })), [data.fields]);

  const relatedGroups = useMemo(() => {
    const g = {};
    (data.related || []).forEach((r) => { (g[r.content_type] = g[r.content_type] || []).push(r); });
    return g;
  }, [data.related]);

  const gps = data.fields?.gps;
  const mapSection = sections.find((s) => s.type === "map");

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Helmet>
        <html lang="fr" />
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content={seo.robots || (seo.noindex ? "noindex, nofollow" : "index, follow")} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="DealTravel" />
        <meta property="og:title" content={seo.og_title || metaTitle} />
        <meta property="og:description" content={seo.og_description || metaDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content="fr_FR" />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.og_title || metaTitle} />
        <meta name="twitter:description" content={seo.og_description || metaDesc} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        {(data.json_ld || []).map((obj, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
        ))}
      </Helmet>

      <Nav />

      {/* HERO */}
      <section className="relative min-h-[62vh] md:min-h-[78vh] flex items-end overflow-hidden bg-[#1A1B18]">
        {data.cover_image && (
          <img src={data.cover_image} alt={data.cover_alt || data.title} fetchPriority="high"
               className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 md:px-12 pb-12 md:pb-16 pt-32 text-white">
          {/* Breadcrumb */}
          <nav aria-label="Fil d'ariane" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/70 flex-wrap" data-testid="breadcrumb">
            {(data.breadcrumb || []).map((b, i, arr) => (
              <span key={i} className="flex items-center gap-2">
                {i < arr.length - 1 ? <Link to={b.url} className="hover:text-white transition-colors">{b.name}</Link> : <span className="text-[#E4DCCF]">{b.name}</span>}
                {i < arr.length - 1 && <ChevronRight className="h-3 w-3" />}
              </span>
            ))}
          </nav>
          <p className="label-eyebrow text-[#E4DCCF] mt-6">{data.type_label}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-3 max-w-[20ch]">{data.title}</h1>
          {data.excerpt && <p className="mt-4 text-base md:text-lg text-white/85 max-w-[60ch] leading-relaxed">{data.excerpt}</p>}
          {data.reading_time > 1 && data.content_type !== "faq" && (
            <p className="mt-5 text-xs text-white/60 uppercase tracking-[0.2em] flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> {data.reading_time} min de lecture</p>
          )}
        </div>
      </section>

      {/* FACTS STRIP */}
      {facts.length > 0 && (
        <div className="bg-[#1A1B18] text-white border-t border-white/10" data-testid="facts-strip">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {facts.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.key}>
                  <p className="text-[9px] uppercase tracking-[0.2em] text-[#96836B] flex items-center gap-1.5"><Icon className="h-3 w-3" /> {f.label}</p>
                  <p className="text-sm mt-1 text-white/90">{f.value}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* BODY */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-14">
        <article className="min-w-0 space-y-14">
          {sections.map((s) => (
            <SectionBlock key={s.id} section={s} gps={gps} destinationName={data.title} />
          ))}
          {/* AI-generated illustrations gallery */}
          {data.gallery?.length > 0 && (
            <section data-testid="content-gallery" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.gallery.map((g, i) => (
                <figure key={i} className="overflow-hidden">
                  <img src={g.url} alt={g.alt || data.title} loading="lazy"
                       className="w-full aspect-[4/3] object-cover transition-transform duration-700 hover:scale-105" />
                  {g.alt && <figcaption className="mt-2 text-xs text-[#737373]">{g.alt}</figcaption>}
                </figure>
              ))}
            </section>
          )}
          {/* Map (from gps if no explicit map section) */}
          {gps && !mapSection && <MapBlock gps={gps} title="Carte" />}
        </article>

        {/* Sticky rail: sommaire + CTA */}
        <aside className="hidden lg:block">
          <div className="sticky top-28 space-y-6">
            {data.toc?.length > 1 && (
              <nav className="bg-white border border-[#EAE8E3] p-5" data-testid="toc" aria-label="Sommaire">
                <p className="label-eyebrow flex items-center gap-2 mb-3"><List className="h-3.5 w-3.5" /> Sommaire</p>
                <ul className="space-y-2 text-sm">
                  {data.toc.map((t) => (
                    <li key={t.id}><a href={`#sec-${t.id}`} className="text-[#737373] hover:text-[#1A1B18] transition-colors block">{t.title}</a></li>
                  ))}
                </ul>
              </nav>
            )}
            <CtaCard />
          </div>
        </aside>
      </div>

      {/* Topic cluster (pillar → satellites) */}
      {data.cluster?.length > 0 && (
        <div className="border-t border-[#EAE8E3] bg-white" data-testid="cluster">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 md:py-20 space-y-14">
            <div>
              <p className="label-eyebrow">Tout pour votre voyage</p>
              <h2 className="font-serif-display text-3xl md:text-4xl mt-3">Explorez {data.title} en profondeur</h2>
            </div>
            {data.cluster.map((group) => (
              <div key={group.content_type} data-testid={`cluster-group-${group.content_type}`}>
                <div className="flex items-end justify-between gap-4 mb-6">
                  <h3 className="font-serif-display text-2xl md:text-3xl">{group.label}</h3>
                  <Link to={`/${group.url_prefix}`} className="text-[11px] uppercase tracking-[0.2em] text-[#96836B] hover:text-[#1A1B18] whitespace-nowrap">Tout voir →</Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {group.items.map((it) => (
                    <Link key={it.id} to={it.url} data-testid={`cluster-card-${it.slug}`} className="group bg-[#FAF9F6] border border-[#EAE8E3] overflow-hidden hover:border-[#96836B] transition-colors">
                      {it.cover_image
                        ? <img src={it.cover_image} alt={it.cover_alt || it.title} loading="lazy" className="w-full aspect-[16/10] object-cover" />
                        : <div className="w-full aspect-[16/10] bg-[#EAE8E3]" />}
                      <div className="p-4">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-serif-display text-lg leading-tight">{it.title}</p>
                          <ArrowRight className="h-4 w-4 text-[#96836B] group-hover:translate-x-1 transition-transform shrink-0" />
                        </div>
                        {it.excerpt && <p className="text-sm text-[#737373] mt-2 leading-relaxed line-clamp-2">{it.excerpt}</p>}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Related content (maillage interne) */}
      {Object.keys(relatedGroups).length > 0 && (
        <div className="border-t border-[#EAE8E3] bg-white" data-testid="related">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 space-y-14">
            {Object.entries(relatedGroups).map(([type, items]) => (
              <div key={type}>
                <h2 className="font-serif-display text-2xl md:text-3xl mb-6">{RELATED_TITLES[type] || "À découvrir"}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {items.map((r) => (
                    <Link key={r.to_id} to={r.url} data-testid={`related-${r.to_id}`} className="group bg-[#FAF9F6] border border-[#EAE8E3] overflow-hidden hover:border-[#96836B] transition-colors">
                      {r.cover_image
                        ? <img src={r.cover_image} alt={r.title} loading="lazy" className="w-full aspect-[16/10] object-cover" />
                        : <div className="w-full aspect-[16/10] bg-[#EAE8E3]" />}
                      <div className="p-4 flex items-center justify-between gap-2">
                        <p className="font-serif-display text-lg leading-tight">{r.title}</p>
                        <ArrowRight className="h-4 w-4 text-[#96836B] group-hover:translate-x-1 transition-transform shrink-0" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Final CTA */}
      <FinalCta title={data.title} />

      <Footer />
    </div>
  );
};

const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const inline = (s) =>
  esc(s)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\[([^\]]+)\]\((https?:[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener nofollow">$1</a>');

// Light Markdown → HTML: ## → H3, ### → H4, "- "/"1." lists, **bold**, [links](url), paragraphs.
const richToHtml = (body) => {
  const lines = String(body || "").split("\n");
  let html = "";
  let list = null;
  let para = [];
  const flushPara = () => { if (para.length) { html += `<p>${para.join("<br/>")}</p>`; para = []; } };
  const flushList = () => { if (list) { html += `<${list.tag}>${list.items.join("")}</${list.tag}>`; list = null; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    let m;
    if ((m = line.match(/^###\s+(.*)/))) { flushPara(); flushList(); html += `<h4>${inline(m[1])}</h4>`; }
    else if ((m = line.match(/^##\s+(.*)/))) { flushPara(); flushList(); html += `<h3>${inline(m[1])}</h3>`; }
    else if ((m = line.match(/^[-*]\s+(.*)/))) { flushPara(); if (!list || list.tag !== "ul") { flushList(); list = { tag: "ul", items: [] }; } list.items.push(`<li>${inline(m[1])}</li>`); }
    else if ((m = line.match(/^\d+\.\s+(.*)/))) { flushPara(); if (!list || list.tag !== "ol") { flushList(); list = { tag: "ol", items: [] }; } list.items.push(`<li>${inline(m[1])}</li>`); }
    else { flushList(); para.push(inline(line)); }
  }
  flushPara();
  flushList();
  return html;
};

const SectionBlock = ({ section, gps, destinationName }) => {
  const type = section.type || "richtext";
  const anchor = `sec-${section.id}`;

  if (type === "takeaways") {
    const items = (section.body || "").split("\n").map((x) => x.trim().replace(/^[-*]\s*/, "")).filter(Boolean);
    if (!items.length) return null;
    return (
      <section id={anchor} data-testid={`pub-section-${section.key}`}
        className="scroll-mt-28 bg-white border border-[#EAE8E3] border-l-2 border-l-[#96836B] p-7 md:p-8 shadow-[0_16px_44px_-30px_rgba(26,27,24,0.6)]">
        <p className="label-eyebrow flex items-center gap-2 text-[#96836B]"><Sparkles className="h-3.5 w-3.5" /> {section.title || "L'essentiel"}</p>
        <ul className="mt-4 space-y-3">
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-3">
              <Check className="h-5 w-5 text-[#96836B] mt-0.5 shrink-0" strokeWidth={2} />
              <span className="text-[#1A1B18] leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(it) }} />
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (type === "profile") {
    const hasBody = !!section.body;
    const faqs = (section.items || []).filter((q) => q.question);
    if (!hasBody && !faqs.length) return null;
    const profile = (section.key || "").replace("profil_", "");
    const voyage = { couple: "couple", noces: "noces", famille: "famille", amis: "amis", seniors: "senior",
      solo: "solo", luxe: "luxe", budget: "budget", aventure: "aventure", bienetre: "bien-etre", nomade: "nomade" }[profile] || "";
    const dest = destinationName ? `&dest=${encodeURIComponent(destinationName)}` : "";
    return (
      <section id={anchor} data-testid={`pub-section-${section.key}`} className="scroll-mt-28 border border-[#EAE8E3] bg-[#FAF9F6] p-7 md:p-9">
        {section.title && <h2 className="font-serif-display text-2xl md:text-3xl mb-4">{section.title}</h2>}
        {hasBody && (
          <div className="prose-content text-[#333] leading-relaxed text-[15px] md:text-base space-y-4"
               dangerouslySetInnerHTML={{ __html: richToHtml(section.body) }} />
        )}
        {faqs.length > 0 && (
          <Accordion type="single" collapsible className="border-t border-[#EAE8E3] mt-6">
            {faqs.map((qa, i) => (
              <AccordionItem key={i} value={`p${i}`} className="border-b border-[#EAE8E3]">
                <AccordionTrigger className="text-left font-medium py-4 hover:no-underline">{qa.question}</AccordionTrigger>
                <AccordionContent className="text-[#4d4d4d] leading-relaxed pb-4">
                  <div dangerouslySetInnerHTML={{ __html: richToHtml(qa.answer) }} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
        <a href={`/?voyage=${voyage}${dest}#devis`} data-testid={`profile-cta-${profile}`}
          className="mt-7 inline-flex items-center gap-2 bg-[#1A1B18] text-[#FAF9F6] px-7 py-3.5 text-[11px] uppercase tracking-[0.24em] hover:bg-[#96836B] transition-colors">
          Ce voyage vous inspire ? Créer mon voyage sur-mesure <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </section>
    );
  }

  if (type === "faq") {
    const items = (section.items || []).filter((q) => q.question);
    if (!items.length) return null;
    return (
      <section id={anchor} data-testid={`pub-section-${section.key}`} className="scroll-mt-28">
        {section.title && <h2 className="font-serif-display text-2xl md:text-3xl mb-6">{section.title}</h2>}
        <Accordion type="single" collapsible className="border-t border-[#EAE8E3]">
          {items.map((qa, i) => (
            <AccordionItem key={i} value={`q${i}`} className="border-b border-[#EAE8E3]">
              <AccordionTrigger className="text-left font-medium py-5 hover:no-underline">{qa.question}</AccordionTrigger>
              <AccordionContent className="text-[#4d4d4d] leading-relaxed pb-5">
                <div dangerouslySetInnerHTML={{ __html: richToHtml(qa.answer) }} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    );
  }

  if (type === "cta") {
    const label = section.config?.button_label || "Demander un devis";
    const href = section.config?.href || "/#devis";
    return (
      <section id={anchor} data-testid={`pub-section-${section.key}`} className="scroll-mt-28 bg-[#1A1B18] text-white p-8 md:p-10 text-center">
        {section.title && <h2 className="font-serif-display text-2xl md:text-3xl">{section.title}</h2>}
        {section.body && <p className="mt-3 text-white/80 max-w-[50ch] mx-auto">{section.body}</p>}
        <a href={href} className="inline-block mt-6 bg-[#E4DCCF] text-[#1A1B18] px-8 py-3.5 text-[11px] uppercase tracking-[0.24em] hover:bg-white transition-colors">{label}</a>
      </section>
    );
  }

  if (type === "list") {
    const items = (section.body || "").split("\n").map((x) => x.trim()).filter(Boolean);
    if (!items.length && !section.title) return null;
    return (
      <section id={anchor} data-testid={`pub-section-${section.key}`} className="scroll-mt-28">
        {section.title && <h2 className="font-serif-display text-2xl md:text-3xl mb-6">{section.title}</h2>}
        <ul className="space-y-3">
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-3">
              <Check className="h-5 w-5 text-[#96836B] mt-0.5 shrink-0" strokeWidth={2} />
              <span className="text-[#333] leading-relaxed">{it}</span>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (type === "map") {
    const q = section.config?.query || gps;
    if (!q) return null;
    return <MapBlock gps={q} title={section.title} id={anchor} />;
  }

  // richtext (default)
  if (!section.body && !section.title) return null;
  return (
    <section id={anchor} data-testid={`pub-section-${section.key}`} className="scroll-mt-28">
      {section.title && <h2 className="font-serif-display text-2xl md:text-3xl mb-5">{section.title}</h2>}
      <div className="prose-content text-[#333] leading-relaxed text-[15px] md:text-base space-y-4"
           dangerouslySetInnerHTML={{ __html: richToHtml(section.body) }} />
    </section>
  );
};

const MapBlock = ({ gps, title, id }) => (
  <section id={id} className="scroll-mt-28">
    {title && <h2 className="font-serif-display text-2xl md:text-3xl mb-5">{title}</h2>}
    <div className="border border-[#EAE8E3] overflow-hidden aspect-[16/9]">
      <iframe
        title={title || "Carte"}
        loading="lazy"
        src={`https://maps.google.com/maps?q=${encodeURIComponent(gps)}&z=6&output=embed`}
        className="w-full h-full"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  </section>
);

const CtaCard = () => (
  <div className="bg-[#1A1B18] text-white p-6" data-testid="cta-card">
    <p className="label-eyebrow text-[#E4DCCF]">Voyage sur mesure</p>
    <p className="font-serif-display text-2xl mt-2 leading-tight">Recevez votre devis personnalisé</p>
    <p className="text-sm text-white/70 mt-3">Nos conseillers composent votre itinéraire selon vos envies et votre budget.</p>
    <a href="/#devis" data-testid="cta-card-btn" className="mt-5 block text-center bg-[#E4DCCF] text-[#1A1B18] py-3 text-[11px] uppercase tracking-[0.24em] hover:bg-white transition-colors">Demander un devis</a>
  </div>
);

const FinalCta = ({ title }) => (
  <section className="bg-[#96836B]" data-testid="final-cta">
    <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 md:py-20 text-center text-white">
      <p className="label-eyebrow text-white/80">Prêt à partir ?</p>
      <h2 className="font-serif-display text-3xl md:text-4xl mt-3 max-w-[24ch] mx-auto">Composons ensemble votre voyage {title ? `— ${title}` : ""}</h2>
      <a href="/#devis" className="inline-block mt-8 bg-[#1A1B18] text-white px-10 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-black transition-colors">Demander un devis gratuit</a>
    </div>
  </section>
);
